import { FormControlLabel, Switch } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { MuiEvent } from '@mui/x-data-grid-pro';
import { GridStateColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import React from 'react';
import DropzoneTextArea from '../../components/DropzoneTextArea';
import I18n from '../../i18n/I18n';
import DataGrid from '../../list/DataGrid';
import upload from '../../utils/upload';

export type FileUploadSectionProps = {
    EmptyNoRowsOverlay: React.ComponentType;
    feature: string;
    defaultColumns: GridColDef[];
    setDefaultColumns: React.Dispatch<React.SetStateAction<GridColDef[]>>;
    columnVisibility: GridColumnVisibilityModel;
    text: string | string[];
    setText: React.Dispatch<React.SetStateAction<string | string[]>>;
    placeholder: string;
    activeOrganizationAccount: string;
    openUploadDialog: boolean;
    setOpenUploadDialog: React.Dispatch<React.SetStateAction<boolean>>;
    setIsDataImported: React.Dispatch<React.SetStateAction<boolean>>;
    disableTextBasedSelector: boolean;
    hasDataToImport: boolean;
    hasHeaders: boolean;
    setHasHeaders: React.Dispatch<React.SetStateAction<boolean>>;
    isNotReady?: boolean;
    altColDefLabel?: React.ReactNode;
    isAltColDef?: boolean;
    setIsAltColDef?: React.Dispatch<React.SetStateAction<boolean>>;
    externalColumnVisibilityModel?: GridColumnVisibilityModel;
    externalOnColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
    accept?: Record<string, string[]>;
    uploadUrl?: string;
    onUploadSuccess?: (res: { data?: { reference?: { id?: string } } }, file: File) => boolean | void;
    onFileUploaded?: (file: File) => void;
    hideHasHeadersToggle?: boolean;
};

export const PROCESS_URL = '/storage/v1/process';

export const MIME_TO_EXT = {
    'text/csv': ['.csv', '.tsv'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls']
};

export const EML_MIME_TO_EXT = {
    'message/rfc822': ['.eml']
};

export default function FileUploadSection({
    EmptyNoRowsOverlay,
    feature,
    defaultColumns,
    setDefaultColumns,
    columnVisibility,
    text,
    setText,
    placeholder,
    activeOrganizationAccount,
    openUploadDialog,
    setOpenUploadDialog,
    setIsDataImported,
    disableTextBasedSelector,
    hasDataToImport,
    hasHeaders,
    setHasHeaders,
    altColDefLabel,
    isAltColDef,
    setIsAltColDef,
    externalColumnVisibilityModel,
    externalOnColumnVisibilityModelChange,
    accept = MIME_TO_EXT,
    uploadUrl = PROCESS_URL,
    onUploadSuccess,
    onFileUploaded,
    hideHasHeadersToggle = false,
    isNotReady = false
}: FileUploadSectionProps) {
    const handleAltColDefToggle = (_event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => {
        setIsAltColDef?.(newValue);
    };

    return (
        <>
            <DataGrid
                id={'data-import-grid'}
                slots={{
                    noRowsOverlay: EmptyNoRowsOverlay
                }}
                containerSx={{
                    minHeight: '56px',
                    width: '100%',
                    flexGrow: 0,
                    '.MuiDataGrid-overlay': {
                        top: 8
                    },
                    '.MuiDataGrid-footerContainer': {
                        border: 'none'
                    },
                    '.MuiDataGrid-virtualScroller': {
                        overflow: 'hidden'
                    },
                    '.MuiDataGrid-main': {
                        overflow: 'visible'
                    }
                }}
                feature={feature}
                rowCount={0}
                rows={[]}
                hideFooter
                columns={defaultColumns.map((c) => ({ ...c, sortable: false }))}
                defaultColumnVisibility={columnVisibility}
                serverPagination={false}
                includeActions={false}
                disablePagination={true}
                loading={isNotReady}
                hideHeader={isNotReady}
                onColumnOrderChange={(colDefs: GridStateColDef[], event: MuiEvent) => {
                    setDefaultColumns(colDefs);
                    event.defaultMuiPrevented = true;
                }}
                externalColumnVisibilityModel={externalColumnVisibilityModel}
                externalOnColumnVisibilityModelChange={externalOnColumnVisibilityModelChange}
            />
            <DropzoneTextArea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onDropRead={(newText) => setText(newText)}
                placeholder={placeholder}
                converter={(file) => {
                    return upload(activeOrganizationAccount, uploadUrl, file);
                }}
                onError={(msg) => {
                    console.error(msg);
                }}
                triggerUploadDialog={openUploadDialog}
                setOpenUploadDialog={setOpenUploadDialog}
                accept={accept}
                setIsDataImported={setIsDataImported}
                disableTextBasedSelector={disableTextBasedSelector}
                onUploadSuccess={onUploadSuccess}
                onFileUploaded={onFileUploaded}
            />
            {!hasDataToImport && !hideHasHeadersToggle && (
                <FormControlLabel
                    sx={{ flexGrow: 0, ml: 0 }}
                    control={<Switch size={'small'} />}
                    label={<I18n token="import.contacts.uploadfile.hasheaders" />}
                    checked={hasHeaders}
                    onChange={(event, newValue) => setHasHeaders(newValue)}
                />
            )}
            {altColDefLabel && (
                <FormControlLabel
                    sx={{ flexGrow: 0, ml: 0 }}
                    control={<Switch size={'small'} />}
                    label={altColDefLabel}
                    checked={Boolean(isAltColDef)}
                    onChange={handleAltColDefToggle}
                />
            )}
        </>
    );
}
